/* Native Shopify contact submission remains the source of truth for receipt.
   Drafts exist only for a pending submission, in this tab, for up to 30 minutes. */
if (!customElements.get('yatdim-supplier-application')) {
  customElements.define('yatdim-supplier-application', class extends HTMLElement {
    #controller;
    #form;
    #pending = false;
    #draftKey;
    #timer;

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.#form = this.querySelector('form');
      if (!this.#form) return;
      this.#draftKey = `yatdim-supplier-pending:${location.pathname}:${this.#form.id}`;
      if (this.querySelector('[data-success]')) {
        this.#clearDraft();
        this.querySelector('[data-success]').focus();
        return;
      }
      this.#restoreDraft();
      const submitLabel = this.querySelector('[data-submit-label]');
      submitLabel.dataset.original = submitLabel.textContent;
      // Validate before Shopify's contact/CAPTCHA submit listeners. Those
      // listeners can call form.submit() even after preventDefault().
      this.#form.addEventListener('submit', event => this.#submit(event), { signal, capture: true });
      this.#form.addEventListener('invalid', event => {
        event.preventDefault();
        this.#validate()?.focus();
      }, { signal, capture: true });
      this.#form.noValidate = false;
      this.#form.addEventListener('input', event => {
        if (this.#pending) return;
        const field = event.target;
        if (field.matches('[data-field]')) this.#showFieldError(field, '');
        if (field.matches('[data-choice]')) this.#validateChoices(field.dataset.choice, false);
        this.#toggleOther();
      }, { signal });
      window.addEventListener('pageshow', () => this.#reset(), { signal });
      this.#toggleOther();
      for (const group of ['categories', 'dropshipping']) this.#validateChoices(group, false);
      const serverError = this.querySelector('[data-submit-error]:not([hidden])');
      if (serverError) serverError.focus();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      clearTimeout(this.#timer);
    }

    #toggleOther() {
      const selected = this.querySelector('[data-choice="categories"][value="other"]:checked');
      const region = this.querySelector('[data-other-category]');
      if (!region) return;
      region.hidden = !selected;
      region.querySelector('input').disabled = !selected;
    }

    #showFieldError(field, message) {
      const error = this.querySelector(`[data-error-for="${field.dataset.field}"]`);
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      field.setAttribute('aria-invalid', String(Boolean(message)));
      if (message) field.setAttribute('aria-describedby', error.id);
      else field.removeAttribute('aria-describedby');
    }

    #validateChoices(group, show = true) {
      const fields = [...this.querySelectorAll(`[data-choice="${group}"]`)];
      const error = this.querySelector(`[data-error-for="${group}"]`);
      if (!fields.length || !error) return true;
      const required = group === 'categories' || group === 'dropshipping';
      const valid = !required || fields.some(field => field.checked);
      fields[0].setCustomValidity(valid ? '' : (group === 'categories' ? this.dataset.categoriesError : this.dataset.selectError));
      if (show || valid) {
        error.hidden = valid;
        error.textContent = valid ? '' : (group === 'categories' ? this.dataset.categoriesError : this.dataset.selectError);
        for (const field of fields) {
          field.setAttribute('aria-invalid', String(!valid));
          if (!valid) field.setAttribute('aria-describedby', error.id);
          else field.removeAttribute('aria-describedby');
        }
      }
      return valid;
    }

    #validate() {
      let firstInvalid;
      for (const field of this.querySelectorAll('[data-field]:not(:disabled)')) {
        if (field.type !== 'checkbox') field.value = field.value.trim();
        let message = '';
        if (field.required && (field.type === 'checkbox' ? !field.checked : !field.value)) {
          message = field.dataset.field === 'consent' ? this.dataset.consentError : field.tagName === 'SELECT' ? this.dataset.selectError : this.dataset.requiredError;
        } else if (field.validity.typeMismatch) {
          message = field.type === 'email' ? this.dataset.emailError : this.dataset.urlError;
        } else if (!field.validity.valid) {
          message = this.dataset.requiredError;
        }
        this.#showFieldError(field, message);
        if (message && !firstInvalid) firstInvalid = field;
      }
      for (const group of ['categories', 'dropshipping']) {
        if (!this.#validateChoices(group) && !firstInvalid) firstInvalid = this.querySelector(`[data-choice="${group}"]`);
      }
      if (firstInvalid) firstInvalid = this.querySelector('[aria-invalid="true"]') || firstInvalid;
      const summary = this.querySelector('[data-error-summary]');
      summary.hidden = !firstInvalid;
      return firstInvalid;
    }

    #submit(event) {
      if (this.#pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const firstInvalid = this.#validate();
      if (firstInvalid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        firstInvalid.focus();
        return;
      }
      // Let Shopify's native contact/CAPTCHA handler complete the POST. A timer
      // never fabricates a receipt or retries a potentially received application.
      this.#saveDraft();
      this.#pending = true;
      const button = this.querySelector('[data-submit]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.querySelector('[data-submit-label]').textContent = this.dataset.submitting;
      this.#timer = setTimeout(() => {
        this.#reset();
        const error = this.querySelector('[data-submit-error]');
        error.hidden = false;
        error.focus();
      }, 45000);
    }

    #reset() {
      clearTimeout(this.#timer);
      this.#pending = false;
      const button = this.querySelector('[data-submit]');
      if (!button) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      const label = this.querySelector('[data-submit-label]');
      if (!label.dataset.original) label.dataset.original = label.textContent;
      label.textContent = label.dataset.original;
    }

    #saveDraft() {
      try {
        const fields = [...this.#form.querySelectorAll('[data-field],[data-choice]')].filter(field => field.type !== 'checkbox' || field.dataset.field !== 'consent');
        sessionStorage.setItem(this.#draftKey, JSON.stringify({ expires: Date.now() + 30 * 60 * 1000, fields: fields.map(field => ({ name: field.name, value: field.value, checked: field.checked })) }));
      } catch { /* Submission still works with browser storage disabled. */ }
    }

    #restoreDraft() {
      try {
        const draft = JSON.parse(sessionStorage.getItem(this.#draftKey) || 'null');
        this.#clearDraft();
        if (!draft || draft.expires < Date.now()) return;
        for (const item of draft.fields) {
          const fields = [...this.#form.elements].filter(field => field.name === item.name);
          for (const field of fields) {
            if (field.type === 'checkbox' || field.type === 'radio') {
              if (field.value === item.value) field.checked = item.checked;
            } else field.value = item.value;
          }
        }
      } catch { this.#clearDraft(); }
    }

    #clearDraft() {
      try { sessionStorage.removeItem(this.#draftKey); } catch { /* Storage is optional. */ }
    }
  });
}
